import classnames from 'classnames';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { MyLink } from '@/components/MyLink';
import { FPG_ASSETS_STATUS } from '@/helpers/constants';
import { APP_LINKS } from '@/helpers/links';
import { FpgAssetsWarningModal } from '@/modals/FpgAssetsWarningModal';
import { FundraisingCalculation } from '../../components/FundraisingCalculation';
import { FpgAssetsUpload } from '../../components/FpgAssetsUpload';
import { ConfirmBlock } from '../../components/ConfirmBlock';
import { VatWarning } from '../../components/VatWarning';
import { useFpgAssetsRequestWarning } from './useFpgAssetsRequestWarning';
import Icon from './icon_back.svg';
import r from '@/kit/RoundButton/button.module.scss';
import s from './index.module.scss';

export const LenderStepFPGSelect = ({
  status,
  manualTariffSet,
  companyId,
  freeFpgOption,
  disabled,
  onBack,
  onNext,
}) => {
  const { values, isSubmitting, setFieldValue } = useFormikContext();
  const { t } = useTranslation();
  useFpgAssetsRequestWarning();

  const fpgUploadRequired =
    !!freeFpgOption &&
    status?.fpg_assets_status === FPG_ASSETS_STATUS.new &&
    values.fpg?.id === freeFpgOption?.id &&
    freeFpgOption?.requires_documents;

  return (
    <div className={s.wrapper}>
      <div className={s.navigation}>
        <button type="button" onClick={onBack} className={s.btn_back}>
          <img src={Icon} alt="arrow" width={24} height={24} />
          {t('fundraising:step_fpg_select.back')}
        </button>
      </div>
      <div className={s.content}>
        <div className="container-inner">
          <div className={s.inner}>
            <div className={s.header}>
              <h2 className="f-h2 f-500">{t('fundraising:step_fpg_select.fpg_option')}</h2>
              <p className={classnames(s.desc, 'f-label')}>
                {t('fundraising:step_fpg_select.scramble_limits')}{' '}
                <MyLink href={APP_LINKS.loanTermsFpg()}>
                  {t('fundraising:step_fpg_select.learn_more')}
                </MyLink>
              </p>
            </div>
            <div>
              <div className={s.cards}>
                {status?.available_fpg_options?.map(option => {
                  const isActive = option.id === values.fpg?.id;

                  return (
                    <div
                      key={option.id}
                      className={classnames(s.card, isActive && 'active')}
                      onClick={() => setFieldValue('fpg', option)}
                    >
                      <h3 className="f-h3 f-500">{option.title}</h3>
                      <div dangerouslySetInnerHTML={{ __html: option.description }} />
                      {Number(option.amount) ? (
                        <div className={s.fee}>
                          <div className={s.fee_value}>{Number(option.amount)}%</div>
                          <div className={s.fee_label}>
                            {t('fundraising:step_fpg_select.cost_of_principal')}
                          </div>
                        </div>
                      ) : (
                        <div className={s.fee}>
                          <div className={s.fee_value}>{t('fundraising:step_fpg_select.free')}</div>
                        </div>
                      )}

                      {!Number(option.amount) && (
                        <div
                          className={classnames(
                            s.assets_required,
                            isActive && s.assets_required_active,
                          )}
                        >
                          {t('fundraising:step_fpg_select.assets_required')}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <VatWarning className={s.warning} />
            </div>
            {fpgUploadRequired && (
              <FpgAssetsUpload manualTariffSet={manualTariffSet} freeFpgOption={freeFpgOption} companyId={companyId} />
            )}
            <FundraisingCalculation manualTariffSet={manualTariffSet} companyId={companyId} />
            {status.available_flexibility_options.length ? (
              <div className={s.buttons}>
                <button
                  type="button"
                  className={r.black_large}
                  onClick={onNext}
                  disabled={
                    disabled || isSubmitting || (fpgUploadRequired && !values.fpg_assets.length)
                  }
                >
                  {fpgUploadRequired && !values.fpg_assets.length
                    ? t('fundraising:step_fpg_select.upload_required_docs')
                    : t('fundraising:step_fpg_select.next')}
                </button>
              </div>
            ) : (
              <ConfirmBlock
                disabled={
                  disabled || isSubmitting || (fpgUploadRequired && !values.fpg_assets.length)
                }
                label={
                  fpgUploadRequired &&
                  !values.fpg_assets.length &&
                  t('fundraising:step_fpg_select.upload_required_docs')
                }
              />
            )}
          </div>
        </div>
      </div>
      <FpgAssetsWarningModal />
    </div>
  );
};
